import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (Array.isArray(obj)) {
    return obj?.map(v => toCamelCase(v));
  } else if (obj !== null && obj?.constructor === Object) {
    return Object.keys(obj)?.reduce((result, key) => {
      const camelKey = key?.replace(/_([a-z])/g, (g) => g?.[1]?.toUpperCase());
      result[camelKey] = toCamelCase(obj?.[key]);
      return result;
    }, {});
  }
  return obj;
};

function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

export const reportingService = {
  async getRevenueByDateRange(startDate, endDate) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          folio_ot,
          fecha_ingreso,
          total,
          estado_ot,
          assigned_to,
          vehicle:vehicles(
            tipo
          )
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate)
        ?.lte('fecha_ingreso', endDate)
        ?.order('fecha_ingreso', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching revenue data:', error?.message);
        return [];
      }

      return toCamelCase(data || []);
    } catch (error) {
      console.error('Failed to fetch revenue by date range:', error);
      throw error;
    }
  },

  async getRevenueByTechnician(startDate, endDate) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          folio_ot,
          total,
          fecha_ingreso,
          assigned_to,
          technician:technician_profiles!assigned_to(
            id,
            user:user_profiles(
              full_name,
              email
            )
          )
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate)
        ?.lte('fecha_ingreso', endDate)
        ?.not('assigned_to', 'is', null);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching technician revenue:', error?.message);
        return [];
      }

      const workOrders = toCamelCase(data || []);
      
      // Aggregate by technician
      const technicianMap = {};
      workOrders?.forEach(order => {
        const techId = order?.assignedTo;
        const techName = order?.technician?.user?.fullName || order?.technician?.user?.email || 'Sin asignar';
        
        if (!technicianMap?.[techId]) {
          technicianMap[techId] = {
            technicianId: techId,
            technicianName: techName,
            totalRevenue: 0,
            completedOrders: 0,
            averageTicket: 0
          };
        }
        
        technicianMap[techId].totalRevenue += parseFloat(order?.total || 0);
        technicianMap[techId].completedOrders += 1;
      });

      // Calculate averages and sort
      const technicianStats = Object.values(technicianMap)?.map(tech => ({
        ...tech,
        averageTicket: tech?.completedOrders > 0 ? tech?.totalRevenue / tech?.completedOrders : 0
      }))?.sort((a, b) => b?.totalRevenue - a?.totalRevenue);

      return technicianStats;
    } catch (error) {
      console.error('Failed to fetch revenue by technician:', error);
      throw error;
    }
  },

  async getRevenueByVehicleType(startDate, endDate) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          folio_ot,
          total,
          fecha_ingreso,
          vehicle:vehicles(
            tipo
          )
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate)
        ?.lte('fecha_ingreso', endDate);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching vehicle type revenue:', error?.message);
        return [];
      }

      const workOrders = toCamelCase(data || []);
      
      // Categorize by Auto vs Moto
      const vehicleTypeMap = {
        auto: { type: 'Auto', totalRevenue: 0, count: 0, averageTicket: 0 },
        moto: { type: 'Moto', totalRevenue: 0, count: 0, averageTicket: 0 }
      };

      workOrders?.forEach(order => {
        const vehicleType = order?.vehicle?.tipoVehiculo?.toLowerCase();
        const revenue = parseFloat(order?.total || 0);
        
        if (vehicleType === 'motocicleta') {
          vehicleTypeMap.moto.totalRevenue += revenue;
          vehicleTypeMap.moto.count += 1;
        } else {
          vehicleTypeMap.auto.totalRevenue += revenue;
          vehicleTypeMap.auto.count += 1;
        }
      });

      // Calculate averages
      const vehicleStats = Object.values(vehicleTypeMap)?.map(vType => ({
        ...vType,
        averageTicket: vType?.count > 0 ? vType?.totalRevenue / vType?.count : 0
      }));

      return vehicleStats;
    } catch (error) {
      console.error('Failed to fetch revenue by vehicle type:', error);
      throw error;
    }
  },

  async getDailyRevenueTrend(startDate, endDate) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select('fecha_ingreso, total')
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', startDate)
        ?.lte('fecha_ingreso', endDate)
        ?.order('fecha_ingreso', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') return [];
        console.log('Error fetching daily revenue trend:', error?.message);
        return [];
      }

      // Group by date
      const revenueMap = {};
      data?.forEach(order => {
        const date = new Date(order?.fecha_ingreso)?.toISOString()?.split('T')?.[0];
        if (!revenueMap?.[date]) {
          revenueMap[date] = 0;
        }
        revenueMap[date] += parseFloat(order?.total || 0);
      });

      // Convert to array
      const trendData = Object.keys(revenueMap)?.map(date => ({
        date,
        revenue: revenueMap?.[date]
      }));

      return trendData;
    } catch (error) {
      console.error('Failed to fetch daily revenue trend:', error);
      throw error;
    }
  },

  async getDetailedReportData(startDate, endDate) {
    try {
      const [revenueData, technicianData, vehicleTypeData, trendData] = await Promise.all([
        this.getRevenueByDateRange(startDate, endDate),
        this.getRevenueByTechnician(startDate, endDate),
        this.getRevenueByVehicleType(startDate, endDate),
        this.getDailyRevenueTrend(startDate, endDate)
      ]);

      const totalRevenue = revenueData?.reduce((sum, order) => sum + parseFloat(order?.total || 0), 0);
      const totalOrders = revenueData?.length;
      const averageOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;

      return {
        summary: {
          totalRevenue,
          totalOrders,
          averageOrderValue,
          dateRange: { startDate, endDate }
        },
        technicianBreakdown: technicianData,
        vehicleTypeBreakdown: vehicleTypeData,
        dailyTrend: trendData,
        rawData: revenueData
      };
    } catch (error) {
      console.error('Failed to fetch detailed report data:', error);
      throw error;
    }
  },

  async getWorkOrderFinancialDetails(workOrderId) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          folio_ot,
          fecha_ingreso,
          total,
          estado_ot,
          vehicle:vehicles(
            marca,
            modelo,
            placa,
            tipo
          ),
          client:clients(
            nombre
          ),
          technician:technician_profiles!assigned_to(
            user:user_profiles(
              full_name,
              email
            )
          ),
          items:quotation_items(
            descripcion,
            tipo,
            categoria,
            cantidad,
            precio_unitario,
            subtotal
          ),
          invoice:invoices(
            estado_pago
          )
        `)
        ?.eq('id', workOrderId)
        ?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching work order details:', error?.message);
        return null;
      }

      const workOrder = toCamelCase(data);
      const total = parseFloat(workOrder?.total || 0);
      const subtotal = total / 1.16; // Assuming 16% IVA
      const iva = total - subtotal;

      return {
        id: workOrder?.id,
        folioOt: workOrder?.folioOt,
        fechaIngreso: workOrder?.fechaIngreso,
        total,
        subtotal,
        iva,
        vehicleInfo: `${workOrder?.vehicle?.marca || ''} ${workOrder?.vehicle?.modelo || ''} (${workOrder?.vehicle?.placa || ''})`?.trim(),
        clientName: workOrder?.client?.nombre || 'Sin cliente',
        technicianName: workOrder?.technician?.user?.fullName || workOrder?.technician?.user?.email || 'Sin asignar',
        items: workOrder?.items?.map(item => ({
          descripcion: item?.descripcion,
          tipo: item?.tipo,
          categoria: item?.categoria,
          cantidad: item?.cantidad,
          precioUnitario: parseFloat(item?.precioUnitario || 0),
          subtotal: parseFloat(item?.subtotal || 0)
        })) || [],
        invoiceStatus: workOrder?.invoice?.[0]?.estadoPago || null
      };
    } catch (error) {
      console.error('Failed to fetch work order financial details:', error);
      throw error;
    }
  },

  async getDailyWorkOrders(date) {
    try {
      const { data, error } = await supabase
        ?.from('work_orders')
        ?.select(`
          id,
          folio_ot,
          fecha_ingreso,
          total,
          vehicle:vehicles(
            marca,
            modelo,
            placa
          ),
          client:clients(
            nombre
          ),
          technician:technician_profiles!assigned_to(
            user:user_profiles(
              full_name,
              email
            )
          )
        `)
        ?.eq('estado_ot', 'entregado')
        ?.gte('fecha_ingreso', date)
        ?.lt('fecha_ingreso', new Date(new Date(date).getTime() + 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0])
        ?.order('fecha_ingreso', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        console.log('Error fetching daily work orders:', error?.message);
        return [];
      }

      const workOrders = toCamelCase(data || []);
      return workOrders?.map(order => ({
        id: order?.id,
        folioOt: order?.folioOt,
        fechaIngreso: order?.fechaIngreso,
        total: parseFloat(order?.total || 0),
        vehicleInfo: `${order?.vehicle?.marca || ''} ${order?.vehicle?.modelo || ''} - ${order?.vehicle?.placa || ''}`?.trim(),
        clientName: order?.client?.nombre || 'Sin cliente',
        technicianName: order?.technician?.user?.fullName || order?.technician?.user?.email || 'Sin asignar'
      }));
    } catch (error) {
      console.error('Failed to fetch daily work orders:', error);
      throw error;
    }
  }
};
